import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SavingsCalculator = () => {
  const [inputData, setInputData] = useState({
    initialDeposit: '',
    monthlyContribution: '',
    interestRate: '',
    investmentTerm: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [savingsData, setSavingsData] = useState([]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateSavings = () => {
    const { initialDeposit, monthlyContribution, interestRate, investmentTerm } = inputData;

    // Validate inputs
    if (!initialDeposit || !monthlyContribution || !interestRate || !investmentTerm) {
      setError('Please fill in all required fields');
      return;
    }

    const cleanInitialDeposit = parseFloat(initialDeposit.replace(/\s/g, ''));
    const cleanMonthlyContribution = parseFloat(monthlyContribution.replace(/\s/g, ''));
    const cleanInterestRate = parseFloat(interestRate.replace(/\s/g, '')) / 100; // Convert to decimal
    const cleanInvestmentTerm = parseFloat(investmentTerm.replace(/\s/g, ''));

    if (isNaN(cleanInitialDeposit) || cleanInitialDeposit < 0) {
      setError('Please enter a valid positive number for initial deposit');
      return;
    }

    if (isNaN(cleanMonthlyContribution) || cleanMonthlyContribution < 0) {
      setError('Please enter a valid positive number for monthly contribution');
      return;
    }

    if (isNaN(cleanInterestRate) || cleanInterestRate < 0) {
      setError('Please enter a valid positive number for interest rate');
      return;
    }

    if (isNaN(cleanInvestmentTerm) || cleanInvestmentTerm <= 0) {
      setError('Please enter a valid positive number for investment term');
      return;
    }

    // Calculate monthly interest rate
    const monthlyInterestRate = cleanInterestRate / 12;

    // Calculate total number of months
    const totalMonths = cleanInvestmentTerm * 12;

    // Calculate future value of initial deposit
    let futureValueInitial = cleanInitialDeposit * Math.pow(1 + monthlyInterestRate, totalMonths);

    // Calculate future value of monthly contributions
    let futureValueContributions = 0;
    for (let month = 1; month <= totalMonths; month++) {
      futureValueContributions += cleanMonthlyContribution * Math.pow(1 + monthlyInterestRate, totalMonths - month);
    }

    // Total savings
    const totalSavings = futureValueInitial + futureValueContributions;

    // Calculate total contributions
    const totalContributions = cleanInitialDeposit + (cleanMonthlyContribution * totalMonths);

    // Calculate interest earned
    const interestEarned = totalSavings - totalContributions;

    // Generate year-by-year data
    const yearlyData = [];
    let currentBalance = cleanInitialDeposit;
    const years = Math.floor(totalMonths / 12);

    for (let year = 1; year <= years; year++) {
      let yearEndBalance = currentBalance;
      for (let month = 1; month <= 12; month++) {
        // Add monthly contribution
        currentBalance += cleanMonthlyContribution;
        // Apply monthly interest
        currentBalance += currentBalance * monthlyInterestRate;
      }
      yearlyData.push({
        year,
        startingBalance: year === 1 ? cleanInitialDeposit.toFixed(2) : yearlyData[year - 2]?.endingBalance,
        contributions: (cleanMonthlyContribution * 12).toFixed(2),
        interest: (currentBalance - yearEndBalance).toFixed(2),
        endingBalance: currentBalance.toFixed(2)
      });
    }

    // Create visualization data
    const chartLabels = yearlyData.map(item => `Year ${item.year}`);
    const chartData = {
      labels: chartLabels,
      datasets: [
        {
          label: 'Savings Growth',
          data: yearlyData.map(item => parseFloat(item.endingBalance)),
          fill: false,
          borderColor: 'rgba(75, 192, 192, 1)',
          tension: 0.1
        }
      ]
    };

    setResults({
      initialDeposit: cleanInitialDeposit,
      monthlyContribution: cleanMonthlyContribution,
      interestRate: cleanInterestRate * 100, // Convert back to percentage
      investmentTerm: cleanInvestmentTerm,
      totalSavings: totalSavings.toFixed(2),
      totalContributions: totalContributions.toFixed(2),
      interestEarned: interestEarned.toFixed(2),
      chartData
    });

    setSavingsData(yearlyData);
  };

  const exampleData = {
    initialDeposit: '1000',
    monthlyContribution: '500',
    interestRate: '5',
    investmentTerm: '10'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-green-100 to-teal-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Savings Calculator | Financial Growth Tool</title>
          <meta name="description" content="Calculate your savings growth over time with this tool. Track how your money grows with interest and regular contributions." />
          <meta name="keywords" content="savings calculator, investment calculator, financial growth, compound interest" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-green-600 mr-4">💰</div>
            <h1 className="text-3xl font-bold text-gray-800">Savings Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate how your savings will grow over time with compound interest and regular contributions. This tool helps you plan for financial goals.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Initial Deposit:</label>
              <input
                type="text"
                name="initialDeposit"
                value={inputData.initialDeposit}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter initial deposit (e.g., 1000)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Contribution:</label>
              <input
                type="text"
                name="monthlyContribution"
                value={inputData.monthlyContribution}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter monthly contribution (e.g., 500)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Annual Interest Rate (%):</label>
              <input
                type="text"
                name="interestRate"
                value={inputData.interestRate}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter interest rate (e.g., 5)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Investment Term (Years):</label>
              <input
                type="text"
                name="investmentTerm"
                value={inputData.investmentTerm}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter investment term (e.g., 10)"
              />
            </div>
          </div>

          <button
            onClick={calculateSavings}
            className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Savings
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Savings Summary</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Input Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Initial Deposit</span>
                      <span>${results.initialDeposit.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly Contribution</span>
                      <span>${results.monthlyContribution.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Interest Rate</span>
                      <span>{results.interestRate.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Investment Term</span>
                      <span>{results.investmentTerm} years</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Results</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Contributions</span>
                      <span className="font-bold text-green-600">${results.totalContributions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Interest Earned</span>
                      <span className="font-bold text-green-600">${results.interestEarned}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Savings</span>
                      <span className="font-bold text-green-600">${results.totalSavings}</span>
                    </div>
                  </div>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Savings Growth Over Time</h3>
                  <Line
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Balance ($)'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Year'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Savings Growth Projection'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Yearly Breakdown</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Starting Balance</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contributions</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ending Balance</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {savingsData.map((year, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{year.year}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${year.startingBalance}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${year.contributions}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${year.interest}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold">${year.endingBalance}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-green-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for savings growth:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Initial Deposit: ${exampleData.initialDeposit}</li>
                <li>Monthly Contribution: ${exampleData.monthlyContribution}</li>
                <li>Interest Rate: {exampleData.interestRate}%</li>
                <li>Investment Term: {exampleData.investmentTerm} years</li>
              </ul>

              <h4 className="mb-4">Results:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Total Contributions: $61,000</li>
                <li>Interest Earned: $22,543.49</li>
                <li>Total Savings: $83,543.49</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-green-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Savings Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Initial Deposit:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input your starting balance or initial deposit</li>
                </ul>
              </li>
              <li><strong>Enter Monthly Contribution:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Specify how much you plan to contribute each month</li>
                </ul>
              </li>
              <li><strong>Enter Interest Rate:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the expected annual interest rate</li>
                </ul>
              </li>
              <li><strong>Enter Investment Term:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Specify how many years you plan to save</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will:
                <ul className="list-disc pl-6 mt-2">
                  <li>Show your total savings projection</li>
                  <li>Display a yearly breakdown of your savings</li>
                  <li>Show a visual representation of your savings growth</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-green-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Start saving early to benefit from compound interest</li>
                <li>Consistently increase your monthly contributions</li>
                <li>Choose accounts with higher interest rates</li>
                <li>Automate your savings to stay consistent</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Savings Tips <span className="text-green-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Pay Yourself First:</strong> Treat savings as a non-negotiable expense</li>
              <li><strong>Set Clear Goals:</strong> Define short-term and long-term financial objectives</li>
              <li><strong>Emergency Fund:</strong> Save 3-6 months' expenses for unexpected situations</li>
              <li><strong>Review and Adjust:</strong> Regularly review your savings plan and adjust as needed</li>
            </ul>
            <p className="italic mt-4">
              Note: These calculations are for illustrative purposes only. Actual results may vary based on market conditions and fees.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SavingsCalculator;